/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition-coldef
 * @desc Service for managing column definitions in SmartTable
 */

import { colDefBulkSelect } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/bulk-select';
import { getColDefEntityTemplate } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/entity/template';
import { getMetadataColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/metadata';
import { getMetadataMoleculeColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/metadata/molecule';
import { colDefProtocolConditionAddColumn } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-condition/add-column';
import { colDefProtocolConditionRowNumber } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-condition/row-number';
import { getProtocolValueAmountColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/amount';
import { getProtocolValueResourceColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resource';
import { getProtocolValueResourceItemColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resourceItem';
import { getProtocolValueResourceLocationColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resourceLocation';
import { getProtocolValueResourceItemStatusColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/status';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import type { EntityView } from 'labstep-web/models/entity-view.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { forEach } from 'lodash';

export class GridSmartTableProtocolConditionColDefService {
  public static getDefaultColIds = (
    entity: Experiment | Protocol,
  ) => {
    const defaultColdefs = ['row_number'];

    if (entity?.protocol_condition_variables) {
      entity.protocol_condition_variables
        .filter(
          (variable) =>
            !variable.variable_template &&
            !variable.variable_template_guid,
        )
        .forEach((variable: any) => {
          if (variable instanceof Metadata) {
            defaultColdefs.push(
              `metadata:experiment:${variable.type}:${variable.label}`,
            );
          }
          if (variable instanceof ProtocolValue) {
            defaultColdefs.push(
              `protocol_value:${variable.reportingName}:amount`,
              `protocol_value:${variable.reportingName}:resource_item`,
            );
          }
        });
    }

    return defaultColdefs;
  };

  public static getColDefs = (
    entityView: EntityView,
    experimentOrProtocol: Experiment | Protocol,
    fullscreen: boolean = false,
  ): SmartTableColDef[] => {
    const colDefs: SmartTableColDef[] = [
      colDefProtocolConditionRowNumber,
    ];

    if (fullscreen) {
      colDefs.unshift(colDefBulkSelect);
      if (entityView.name !== 'Conditions Table') {
        colDefs.push(colDefProtocolConditionAddColumn());
      }
    }
    if (!entityView.column_definition_ids) {
      return colDefs;
    }

    forEach(entityView.column_definition_ids, (colId: string) => {
      if (
        colId.startsWith('protocol_value:') &&
        colId.split(':').length === 3
      ) {
        const parts = colId.split(':');
        const protocolValueName = parts[1];
        const fieldType = parts[2];

        if (fieldType === 'resource_item') {
          colDefs.push(
            getProtocolValueResourceItemColumnDef(
              experimentOrProtocol,
              protocolValueName,
            ),
          );
        }
        if (fieldType === 'resource') {
          colDefs.push(
            getProtocolValueResourceColumnDef(
              experimentOrProtocol,
              protocolValueName,
            ),
          );
        }
        if (fieldType === 'amount') {
          colDefs.push(
            getProtocolValueAmountColumnDef(
              experimentOrProtocol,
              protocolValueName,
            ),
          );
        }

        if (fieldType === 'resource_location') {
          colDefs.push(
            getProtocolValueResourceLocationColumnDef(
              experimentOrProtocol,
              protocolValueName,
            ),
          );
        }
        if (fieldType === 'resource_item_status') {
          colDefs.push(
            getProtocolValueResourceItemStatusColDef(
              experimentOrProtocol,
              protocolValueName,
            ),
          );
        }
      } else if (
        colId.split(':').length >= 5 &&
        colId.split(':')[2] === 'molecule'
      ) {
        const parts = colId.split(':');
        const parentEntityClass = parts[1];
        const type = parts[2];
        const metadataLabel = parts[3];
        const field = parts[4];
        const protocolValueName = parts[5];
        const moleculeColDef = getMetadataMoleculeColDef(
          experimentOrProtocol,
          parentEntityClass,
          type as MetadataType,
          metadataLabel,
          field,
          protocolValueName,
        );

        if (moleculeColDef) {
          colDefs.push(moleculeColDef);
        }
      } else if (colId.startsWith('metadata:')) {
        if (colId.startsWith('metadata:experiment:')) {
          const type = Metadata.getTypeFromColId(colId);
          const label = Metadata.getLabelFromColId(colId);
          const colDef = getMetadataColDef(
            experimentOrProtocol,
            Experiment.entityName,
            type,
            label || '',
          );
          colDefs.push(colDef);
        }
        if (colId.startsWith('metadata:protocol:')) {
          const type = Metadata.getTypeFromColId(colId);
          const label = Metadata.getLabelFromColId(colId);
          const colDef = getMetadataColDef(
            experimentOrProtocol,
            Protocol.entityName,
            type,
            label || '',
          );
          colDefs.push(colDef);
        }
        if (colId.startsWith('metadata:resource:')) {
          const type = Metadata.getTypeFromColId(colId);
          const label = Metadata.getLabelFromColId(colId);
          const protocolValueName =
            Metadata.getProtocolValueNameFromColId(colId);

          const colDef = getMetadataColDef(
            experimentOrProtocol,
            Resource.entityName,
            type,
            label || '',
            protocolValueName,
          );
          colDefs.push(colDef);
        }
        if (colId.startsWith('metadata:resource_item:')) {
          const type = Metadata.getTypeFromColId(colId);
          const label = Metadata.getLabelFromColId(colId);
          const protocolValueName =
            Metadata.getProtocolValueNameFromColId(colId);

          const colDef = getMetadataColDef(
            experimentOrProtocol,
            ResourceItem.entityName,
            type,
            label || '',
            protocolValueName,
          );
          colDefs.push(colDef);
        }
      } else if (colId === 'template') {
        colDefs.push(getColDefEntityTemplate(entityView.entity_name));
      }
    });

    return colDefs;
  };
}
